import {View} from 'react-native'
import {Image} from 'expo-image'
import {useLingui} from '@lingui/react'

import {type ArticlePreview} from '#/features/news/types'
import {niceDate} from '#/lib/strings/time'
import {atoms as a, useTheme, web} from '#/alf'
import {Link, type LinkProps} from '#/components/Link'
import {Text} from '#/components/Typography'

type Props = {
  article: ArticlePreview
  categoryTitle?: string
  to: LinkProps['to']
  variant?: 'hero' | 'standard'
}

export function NewsCard({
  article,
  categoryTitle,
  to,
  variant = 'standard',
}: Props) {
  const {i18n} = useLingui()
  const t = useTheme()
  const coverSrc = article.cover?.fullsize || article.cover?.thumb
  const byline = article.author?.displayName || article.author?.handle
  const timestamp = article.publishedAt
    ? niceDate(i18n, article.publishedAt)
    : undefined
  const isHero = variant === 'hero'

  return (
    <Link
      to={to}
      label={article.title}
      style={[a.w_full, a.p_0]}
      hoverStyle={[a.shadow_md, web({transform: [{translateY: -2}]})]}
      variant="ghost"
      shape="round">
      <View
        style={[
          a.overflow_hidden,
          a.border,
          a.shadow_sm,
          t.atoms.border_contrast_low,
          t.atoms.bg_contrast_25,
          {borderRadius: 16},
          web({transition: 'transform 140ms ease, box-shadow 140ms ease'}),
          isHero && a.shadow_md,
        ]}>
        {coverSrc ? (
          <Image
            source={{uri: coverSrc}}
            style={[a.w_full, {aspectRatio: 16 / 9}]}
            contentFit="cover"
            contentPosition="center"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View
            style={[
              a.w_full,
              t.atoms.bg_contrast_50,
              {aspectRatio: 16 / 9},
            ]}
          />
        )}
        <View style={[a.p_md, a.gap_xs]}>
          {categoryTitle ? (
            <Text
              style={[
                a.text_xs,
                a.font_bold,
                t.atoms.text_contrast_medium,
                {letterSpacing: 0.6, textTransform: 'uppercase'},
              ]}>
              {categoryTitle}
            </Text>
          ) : null}
          <Text
            style={[
              a.font_heavy,
              a.leading_snug,
              isHero ? a.text_2xl : a.text_lg,
            ]}
            numberOfLines={2}>
            {article.title}
          </Text>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            {byline ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {byline}
              </Text>
            ) : null}
            {timestamp ? (
              <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                {timestamp}
              </Text>
            ) : null}
          </View>
        </View>
      </View>
    </Link>
  )
}
